import api from './client';

// Media Library API
export const mediaAPI = {
  // Upload media
  upload: (
    formData: FormData,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  ) => api.post('/media/upload', formData, { onUploadProgress }),

  // Create media item from pre-uploaded CDN URL (Spec 158)
  createFromUrl: (data: { fileUrl: string; fileName?: string }) =>
    api.post('/media/upload', data),

  // Get media items with pagination and filtering
  getItems: (params?: {
    page?: number;
    limit?: number;
    type?: 'image' | 'video' | 'all';
    tags?: number[];
    albumId?: number;
    search?: string;
    sort?: 'created_desc' | 'created_asc' | 'taken_desc' | 'taken_asc' | 'name_asc' | 'size_desc';
    fromDate?: string;
    toDate?: string;
  }) => {
    const queryParams = params ? {
      ...params,
      tags: params.tags?.join(','),
    } : undefined;
    return api.get('/media/items', { params: queryParams });
  },

  // Get single media item
  getItem: (uuid: string) => api.get(`/media/items/${uuid}`),

  // Update media item
  updateItem: (uuid: string, data: { title?: string; description?: string; takenAt?: string }) =>
    api.patch(`/media/items/${uuid}`, data),

  // Delete media item
  deleteItem: (uuid: string) => api.delete(`/media/items/${uuid}`),

  // Add tags to media item
  addTags: (uuid: string, tagIds: number[]) =>
    api.post(`/media/items/${uuid}/tags`, { tagIds: tagIds }),

  // Remove tag from media item
  removeTag: (uuid: string, tagId: number) =>
    api.delete(`/media/items/${uuid}/tags/${tagId}`),

  // Tags
  getTags: () => api.get('/media/tags'),

  createTag: (data: { tagType: string; tagValue: string; color?: string }) =>
    api.post('/media/tags', data),

  updateTag: (tagId: number, data: { tagValue?: string; color?: string }) =>
    api.patch(`/media/tags/${tagId}`, data),

  deleteTag: (tagId: number) => api.delete(`/media/tags/${tagId}`),

  // Albums
  getAlbums: () => api.get('/media/albums'),

  createAlbum: (data: { name: string; description?: string }) =>
    api.post('/media/albums', data),

  getAlbum: (albumId: number, params?: { page?: number; limit?: number }) =>
    api.get(`/media/albums/${albumId}`, { params }),

  updateAlbum: (albumId: number, data: { name?: string; description?: string; coverMediaId?: number }) =>
    api.patch(`/media/albums/${albumId}`, data),

  deleteAlbum: (albumId: number) => api.delete(`/media/albums/${albumId}`),

  addItemsToAlbum: (albumId: number, mediaUuids: string[]) =>
    api.post(`/media/albums/${albumId}/items`, { mediaUuids: mediaUuids }),

  removeItemFromAlbum: (albumId: number, mediaUuid: string) =>
    api.delete(`/media/albums/${albumId}/items/${mediaUuid}`),

  reorderAlbumItems: (albumId: number, mediaUuids: string[]) =>
    api.patch(`/media/albums/${albumId}/reorder`, { mediaUuids: mediaUuids }),

  // Search
  search: (query: string, params?: { page?: number; limit?: number; type?: 'image' | 'video' | 'all' }) =>
    api.get('/media/search', { params: { q: query, ...params } }),

  // Storage
  getStorage: () => api.get('/media/storage'),

  // Import from Task
  importFromTask: (completionId: number, fileUrl: string, albumId?: number, newAlbumName?: string) =>
    api.post('/media/import-from-task', {
      completionId: completionId,
      fileUrl: fileUrl,
      albumId: albumId,
      newAlbumName: newAlbumName,
    }),

  // Billing
  getBillingStatus: () => api.get('/media/billing/status'),
  getBillingPlans: () => api.get('/media/billing/plans'),
  checkTrialAvailability: () => api.get('/media/billing/trial/check'),
  startTrial: () => api.post('/media/billing/trial/start'),
  createBillingCheckout: (data: { planType: string; successUrl: string; cancelUrl: string }) =>
    api.post('/media/billing/checkout', data),
  createBillingPortal: (data: { returnUrl: string }) =>
    api.post('/media/billing/portal', data),
  cancelBilling: () => api.post('/media/billing/cancel'),
  reactivateBilling: () => api.post('/media/billing/reactivate'),
  getBillingHistory: () => api.get('/media/billing/history'),

  // User Storage Management
  getStorageWarnings: () => api.get('/media/storage-management/warnings'),
  getStorageAnalytics: () => api.get('/media/storage-management/analytics'),
  getStorageFiles: (params?: {
    page?: number;
    limit?: number;
    type?: 'image' | 'video';
    sortBy?: 'created_at' | 'processed_size_bytes' | 'original_filename';
    sortOrder?: 'asc' | 'desc';
    search?: string;
    includeDeleted?: boolean;
  }) => api.get('/media/storage-management/files', { params }),
  getLargestFiles: (limit?: number) =>
    api.get('/media/storage-management/files/largest', { params: { limit } }),
  softDeleteFile: (itemId: number) =>
    api.delete(`/media/storage-management/files/${itemId}`),
  getRecycleBin: (params?: { page?: number; limit?: number }) =>
    api.get('/media/storage-management/recycle-bin', { params }),
  restoreFile: (itemId: number) =>
    api.post(`/media/storage-management/recycle-bin/${itemId}/restore`),
  permanentlyDeleteFile: (itemId: number) =>
    api.delete(`/media/storage-management/recycle-bin/${itemId}`),
  emptyRecycleBin: () => api.delete('/media/storage-management/recycle-bin'),
  bulkDeleteFiles: (itemIds: number[]) =>
    api.post('/media/storage-management/bulk-delete', { itemIds }),
  deleteOldFiles: (days: number) =>
    api.post('/media/storage-management/delete-old', { days }),

  // Media Sharing
  shareItem: (uuid: string, organizationId: number, userIds: number[]) =>
    api.post(`/media/items/${uuid}/shares`, { organizationId: organizationId, userIds: userIds }),
  getItemShares: (uuid: string) =>
    api.get(`/media/items/${uuid}/shares`),
  removeItemShare: (uuid: string, userId: number) =>
    api.delete(`/media/items/${uuid}/shares/${userId}`),
  getItemsSharedWithMe: (organizationId?: number, params?: { limit?: number; offset?: number }) =>
    api.get('/media/shared-with-me', { params: { organizationId: organizationId, ...params } }),

  // Album Sharing
  shareAlbum: (albumId: number, organizationId: number, userIds: number[]) =>
    api.post(`/media/albums/${albumId}/shares`, { organizationId: organizationId, userIds: userIds }),
  getAlbumShares: (albumId: number) =>
    api.get(`/media/albums/${albumId}/shares`),
  removeAlbumShare: (albumId: number, userId: number) =>
    api.delete(`/media/albums/${albumId}/shares/${userId}`),
  getAlbumsSharedWithMe: (organizationId?: number, params?: { limit?: number; offset?: number }) =>
    api.get('/media/albums/shared-with-me', { params: { organizationId: organizationId, ...params } }),
};
